import {
  useState,
  useEffect,
  useRef,
  type KeyboardEvent,
  type ClipboardEvent,
} from 'react';
import { isQaOrSbx } from '@chikara/utils';
import styles from './OTPInput.module.css';

export interface OTPInputProps {
  length?: number;
  onComplete: (otp: string) => void;
  autoFocus?: boolean;
}

export const OTPInput = ({ length = 6, onComplete, autoFocus = false }: OTPInputProps) => {
  const [values, setValues] = useState<string[]>(Array(length).fill(''));
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);

  const handleFocus = (index: number) => {
    const input = inputsRef.current[index];
    if (input) {
      input.setSelectionRange(0, 1);
    }
  };

  const focusInput = (index: number) => {
    if (index >= 0 && index < length) {
      inputsRef.current[index]?.focus();
    }
  };

  useEffect(() => {
    onComplete(values.join(''));
  }, [values, onComplete]);

  const handleChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;

    const digit = value.slice(-1);

    const newValues = [...values];
    newValues[index] = digit;
    setValues(newValues);

    if (digit && index < length - 1) {
      focusInput(index + 1);
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    const input = inputsRef.current[index];

    if (e.key === 'Backspace') {
      e.preventDefault();

      if (values[index]) {
        const newValues = [...values];
        newValues[index] = '';
        setValues(newValues);

        // mantener foco y seleccionar (clave para Mac)
        setTimeout(() => input?.setSelectionRange(0, 1), 0);
      } else if (index > 0) {
        focusInput(index - 1);

        const newValues = [...values];
        newValues[index - 1] = '';
        setValues(newValues);
      }
    }

    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      focusInput(index - 1);
    }

    if (e.key === 'ArrowRight') {
      e.preventDefault();
      focusInput(index + 1);
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, length);

    if (!/^\d+$/.test(pastedData)) return;

    const newValues = [...values];
    pastedData.split('').forEach((char, idx) => {
      if (idx < length) {
        newValues[idx] = char;
      }
    });
    setValues(newValues);

    if (pastedData.length === length) {
      onComplete(pastedData);
    } else {
      focusInput(pastedData.length);
    }
  };

  useEffect(() => {
    if (autoFocus) {
      focusInput(0);
    }
  }, [autoFocus]);


  return (
    <div className={styles.container}>
      {isQaOrSbx() && (
        <input data-testid="mfa-otp-full" className={styles.fullInput} maxLength={6} style={{display: "block"}} value=""/>
      )}
      {values.map((value, index) => (
        <input
          key={index}
          autoFocus={autoFocus && index === 0}
          ref={el => {
            inputsRef.current[index] = el;
          }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={value}
          onChange={e => handleChange(index, e.target.value)}
          onKeyDown={e => handleKeyDown(index, e)}
          onPaste={handlePaste}
          onFocus={() => handleFocus(index)}
          className={styles.input}
          aria-label={`Dígito ${index + 1}`}
        />
      ))}
    </div>
  );
};
